/**
 * The org's units, handed down to the pages that print a value (units slice 2b). A component that formats a stored
 * value reads `useShownPrefs()`; the page that holds the org's choice mounts `<OrgUnits>` (components/units/OrgUnits)
 * once around its tree. Without one, `useShownPrefs()` is undefined and every value is shown as stored: a piece of a
 * page rendered on its own (a test, a storybook) needs no providers, and a metric org's page is unchanged.
 */
import { createContext, useContext, useMemo } from "react";
import { shownFor, type Shown } from "./display";
import type { DisplayPrefs } from "./prefs";

export const DisplayPrefsContext = createContext<DisplayPrefs | undefined>(undefined);

/** The org's choice where an `OrgUnits` (or a provider of one) is above; undefined means "as stored". */
export function useShownPrefs(): DisplayPrefs | undefined {
  return useContext(DisplayPrefsContext);
}

/** The unit a value stored in `storedUnit` is shown in, and the conversion, under the org's choice. */
export function useShown(storedUnit: string): Shown {
  const prefs = useShownPrefs();
  return useMemo(() => shownFor(storedUnit, prefs), [storedUnit, prefs]);
}
